// Ubicación en el repo: src/components/PrescriptionFormModal.jsx
// Modal Crear/Editar receta de lentes (OFT) — mismo contenido que
// openPrescriptionModal() del prototipo (línea ~2353): paciente, fecha,
// profesional (filtrado por rol OFT + sucursal), Lejos (esfera/cilindro/eje
// OD-OI), AVsc/AVcc, ADD, ADD intermedia, DP, Cerca, Uso, Diagnóstico,
// Reevaluar, Observaciones.
import { useState } from 'react';
import {
  createPrescription,
  updatePrescription,
  DIAGNOSTICO_OPTS,
  USO_OPTS,
  lensRxToInputs,
  lensRxToValues,
  avToInputs,
  avToValues,
} from '../lib/prescriptions';
import { searchPatients } from '../lib/patients';
import { professionalsForModuleAndBranch } from '../lib/professionals';
import { formatPatientName } from '../lib/format';

function initialForm(rx) {
  if (rx) {
    return {
      patientId: rx.patient_id,
      patientLabel: rx.patients ? formatPatientName(rx.patients) : '',
      date: rx.date,
      professionalId: rx.professional_id || '',
      lejos: lensRxToInputs(rx.lejos),
      cerca: lensRxToInputs(rx.cerca),
      av: avToInputs(rx.av),
      addEsf: rx.add_esf ?? '',
      addIntermedia: rx.add_intermedia ?? '',
      dp: rx.dp ?? '',
      uso: rx.uso || '',
      diagnostico: rx.diagnostico || [],
      reevaluar: !!rx.reevaluar,
      notes: rx.notes || '',
    };
  }
  return {
    patientId: null,
    patientLabel: '',
    date: new Date().toLocaleDateString('sv-SE'),
    professionalId: '',
    lejos: lensRxToInputs(null),
    cerca: lensRxToInputs(null),
    av: avToInputs(null),
    addEsf: '',
    addIntermedia: '',
    dp: '',
    uso: '',
    diagnostico: [],
    reevaluar: false,
    notes: '',
  };
}

function LensRxTable({ label, value, onChange }) {
  return (
    <table className="freq-table">
      <thead>
        <tr>
          <th></th>
          <th>Esfera</th>
          <th>Cilindro</th>
          <th>Eje</th>
        </tr>
      </thead>
      <tbody>
        {['od', 'oi'].map((eye) => (
          <tr key={eye}>
            <td className={eye === 'od' ? 'freq-table__od' : 'freq-table__oi'} style={{ fontWeight: 700 }}>
              {eye.toUpperCase()}
            </td>
            <td>
              <input
                type="number"
                step="0.25"
                value={value[eye].esfera}
                onChange={(e) => onChange(eye, 'esfera', e.target.value)}
              />
            </td>
            <td>
              <input
                type="number"
                step="0.25"
                value={value[eye].cilindro}
                onChange={(e) => onChange(eye, 'cilindro', e.target.value)}
              />
            </td>
            <td>
              <input type="text" value={value[eye].eje} onChange={(e) => onChange(eye, 'eje', e.target.value)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AvTable({ value, onChange }) {
  return (
    <table className="freq-table">
      <thead>
        <tr>
          <th></th>
          <th>AVsc</th>
          <th>AVcc</th>
        </tr>
      </thead>
      <tbody>
        {['od', 'oi'].map((eye) => (
          <tr key={eye}>
            <td className={eye === 'od' ? 'freq-table__od' : 'freq-table__oi'} style={{ fontWeight: 700 }}>
              {eye.toUpperCase()}
            </td>
            <td>
              <input type="text" value={value[eye].sc} onChange={(e) => onChange(eye, 'sc', e.target.value)} />
            </td>
            <td>
              <input type="text" value={value[eye].cc} onChange={(e) => onChange(eye, 'cc', e.target.value)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function PrescriptionFormModal({ prescription, branchId, professionals, actorId, onClose, onSaved }) {
  const isEdit = !!prescription;
  const [form, setForm] = useState(() => initialForm(prescription));
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function setLens(group) {
    return (eye, field, value) => {
      setForm((f) => ({ ...f, [group]: { ...f[group], [eye]: { ...f[group][eye], [field]: value } } }));
    };
  }

  function setAv(eye, field, value) {
    setForm((f) => ({ ...f, av: { ...f.av, [eye]: { ...f.av[eye], [field]: value } } }));
  }

  function toggleDiagnostico(d) {
    setForm((f) => ({
      ...f,
      diagnostico: f.diagnostico.includes(d) ? f.diagnostico.filter((x) => x !== d) : [...f.diagnostico, d],
    }));
  }

  function selectPatient(p) {
    set('patientId', p.id);
    setForm((f) => ({ ...f, patientLabel: formatPatientName(p) }));
    setPatientQuery('');
    setPatientResults([]);
  }

  async function handlePatientSearch(value) {
    setPatientQuery(value);
    if (!value.trim()) {
      setPatientResults([]);
      return;
    }
    try {
      setPatientResults(await searchPatients(value));
    } catch {
      setPatientResults([]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.patientId) {
      setError('Selecciona un paciente.');
      return;
    }
    if (!form.date) {
      setError('Indica la fecha.');
      return;
    }
    setSaving(true);
    try {
      const values = {
        patient_id: form.patientId,
        date: form.date,
        professional_id: form.professionalId || null,
        lejos: lensRxToValues(form.lejos),
        cerca: lensRxToValues(form.cerca),
        av: avToValues(form.av),
        add_esf: form.addEsf !== '' ? Number(form.addEsf) : null,
        add_intermedia: form.addIntermedia !== '' ? Number(form.addIntermedia) : null,
        dp: form.dp !== '' ? Number(form.dp) : null,
        uso: form.uso || null,
        diagnostico: form.diagnostico,
        reevaluar: form.reevaluar,
        notes: form.notes.trim() || null,
      };
      if (isEdit) {
        await updatePrescription(prescription.id, values, actorId);
      } else {
        await createPrescription(values, actorId);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la receta.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar receta' : 'Nueva receta de lentes'}</h2>

        <form onSubmit={handleSubmit} className="patient-form">
          <div className="field">
            <span>Paciente *</span>
            {form.patientId ? (
              <div className="patient-picker-selected">
                {form.patientLabel}
                {!isEdit && (
                  <button type="button" className="link-button" onClick={() => set('patientId', null)}>
                    Cambiar
                  </button>
                )}
              </div>
            ) : (
              <>
                <input
                  value={patientQuery}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT…"
                />
                {patientResults.length > 0 && (
                  <ul className="patient-picker-results">
                    {patientResults.map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => selectPatient(p)}>
                          {formatPatientName(p)} {p.rut ? `— ${p.rut}` : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          <div className="form-row">
            <label className="field">
              <span>Fecha *</span>
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
            </label>
            <label className="field">
              <span>Profesional</span>
              <select value={form.professionalId} onChange={(e) => set('professionalId', e.target.value)}>
                <option value="">Sin asignar</option>
                {professionalsForModuleAndBranch(professionals, 'OFT', branchId).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} {p.apellido} ({p.role})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-row">
            <div className="field">
              <span>Lejos</span>
              <LensRxTable value={form.lejos} onChange={setLens('lejos')} />
            </div>
            <div className="field">
              <span>AVsc / AVcc</span>
              <AvTable value={form.av} onChange={setAv} />
            </div>
          </div>

          <div className="form-row">
            <label className="field">
              <span>ADD (ESF.)</span>
              <input type="number" step="0.25" value={form.addEsf} onChange={(e) => set('addEsf', e.target.value)} />
            </label>
            <label className="field">
              <span>ADD intermedia (ESF.)</span>
              <input
                type="number"
                step="0.25"
                value={form.addIntermedia}
                onChange={(e) => set('addIntermedia', e.target.value)}
              />
            </label>
          </div>

          <label className="field">
            <span>DP (mm)</span>
            <input type="number" step="0.5" style={{ maxWidth: 140 }} value={form.dp} onChange={(e) => set('dp', e.target.value)} />
          </label>

          <div className="field">
            <span>Cerca</span>
            <LensRxTable value={form.cerca} onChange={setLens('cerca')} />
          </div>

          <div className="form-row">
            <label className="field">
              <span>Uso</span>
              <select value={form.uso} onChange={(e) => set('uso', e.target.value)}>
                <option value="">Sin indicar</option>
                {USO_OPTS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </label>
            <div className="field">
              <span>Diagnóstico</span>
              <div className="checkbox-group">
                {DIAGNOSTICO_OPTS.map((d) => (
                  <label key={d} className="field--checkbox">
                    <input type="checkbox" checked={form.diagnostico.includes(d)} onChange={() => toggleDiagnostico(d)} />
                    {d}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="field">
            <label className="field--checkbox">
              <input type="checkbox" checked={form.reevaluar} onChange={(e) => set('reevaluar', e.target.checked)} />
              Reevaluar receta / corrección
            </label>
          </div>

          <label className="field">
            <span>Observaciones</span>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Observaciones adicionales…" />
          </label>

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
